// @ts-check
// =============================================================================
// js/screens/duel/duel.js — the duel screen: one PIXI canvas + thin overlays.
//
// WHAT THIS IS (plan/duel.md)
//   The actual game. One screen, three modes: multiplayer, single-player vs a
//   bot (same protocol; the bot runs client-side), and read-only spectating.
//   The game surface — board, hand, resource bars, animations — is a single
//   PIXI.Application filling the viewport (#duel-stage). Text-heavy chrome
//   (the action log, the leave button, press-hold zoom) are thin DOM
//   overlays above the canvas (see log.js for why); the game-over result
//   title renders in PIXI on the fx layer, centered on the battlefield.
//
// STRUCTURE
//   duel.js       this file: lifecycle, layout, zoom, game-over, fullscreen
//   bars.js       the two resource bars (+ face targets, mulligan)
//   board.js      the 4×4 tile grid + highlights
//   hand.js       the hand scroller
//   targeting.js  selection→target state machine (all input funnels here)
//   animations.js the Duel-Update queue + GSAP timelines + audio cues
//
// MOBILE (duel.md directives)
//   On coarse-pointer devices the game-entry buttons (lobby/ladder) already
//   request fullscreen inside their click (ui/fullscreen.js), so the duel
//   usually mounts fullscreen. The first-tap fallback here covers gestureless
//   entries (hosts socket-pushed in, reconnects) and refused requests; iPhone
//   Safari lacks the API and simply keeps the viewport-maximized layout.
//   Fullscreen exits when the screen unmounts. Portrait-first layout: the
//   board scales to fit.
// =============================================================================

import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { store, setSlice, subscribe } from '../../core/store.js';
import { navigate } from '../../core/router.js';
import { setDuelUpdateHandler } from '../../net/socket.js';
import { initCardRenderer, gen, resolveCardArt } from '../../render/card-tile.js';
import { SRC } from '../../render/image-cache.js';
import { ORIGIN_PREFIX } from '../../net/cdn.js';
import { preloadOwnDuelArts } from '../../render/art-preload.js';
import { openModal, escapeHtml } from '../../ui/modal.js';
import { GUIDE_HTML } from '../../data/guide-content.js';
import { requestMobileFullscreenOnFirstTap, exitGameFullscreen } from '../../ui/fullscreen.js';
import { can } from '../../app/session.js';
import { maybeShowQuickplayIntro } from '../../app/quickplay.js';
import { HERO_ASSET_NAMES } from '../../ui/deck-list.js';
import { renderBoard, applyBoardTargeting } from './board.js';
import { renderHand, applyHandTargeting } from './hand.js';
import { renderBars, applyBarTargeting, previewTickSpend } from './bars.js';
import { mountLog, unmountLog, isLogOpen, refreshChrome } from './log.js';
import { onDuelUpdate, setAnimationScene, kickBotIfNeeded } from './animations.js';
import { loadBots } from '../../net/bot-runner.js';
import { ensureStartupBundle } from '../../net/startup-bundle.js';
import { resetTargeting, setTargetingChanged, setActionSent, setFirstTurnBlockedHandler, unlockInput, click } from './targeting.js';

/** The live scene (null when unmounted). */
let scene = /** @type {any} */ (null);

/** @type {HTMLElement|null} */
let stageEl = null;

/** Unsubscribes the empty-screen duel watcher (see render). */
let emptyWatcher = /** @type {(() => void)|null} */ (null);

/**
 * Render the duel screen.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	const duel = store.duel;
	if (!duel) {
		const empty = document.createElement('div');
		empty.className = 'screen';
		empty.innerHTML = `
			<h1 class="screen-title">No Game</h1>
			<div class="empty-state">You're not in a duel. Head to the lobby to start one!</div>`;
		container.appendChild(empty);
		// A page refresh (or the post-restart reload) lands on #/duel BEFORE
		// the socket has re-delivered the live game — the route mounts empty
		// and no navigation will ever re-render it (we're already here). So:
		// when the duel arrives while this empty screen is up, swap it in.
		emptyWatcher = subscribe('duel', (incoming) => {
			if (!incoming) return;
			emptyWatcher?.();
			emptyWatcher = null;
			empty.remove();
			render(container, _params);
		});
		return;
	}

	stageEl = document.createElement('div');
	stageEl.id = 'duel-stage';
	container.appendChild(stageEl);
	mountOverlays(stageEl, duel);
	mountLoadingVeil(stageEl);
	void mountScene(stageEl);

	// Quickplay's one-time welcome sheet (the absolute basics) opens over the
	// board as the auto-started duel mounts. No-ops outside /quickplay.
	maybeShowQuickplayIntro(duel);
}

/** The dark cover held over the stage while the scene assembles (see below). */
let veilEl = /** @type {HTMLElement|null} */ (null);

/**
 * Drop a solid dark-purple cover over the whole stage the instant we enter a
 * duel. The PIXI scene builds piece by piece — canvas, then the battlefield
 * frame, then board tiles, then the hand — and without this the player watches
 * it assemble live. The veil hides that: mountScene fades it out only once the
 * frame art and the first board/hand/bars paint have all landed. Its color is
 * a hair above --bg so it reads as "loading", and it fades to transparent so
 * the finished scene shows through the same dark backdrop with no flash.
 * @param {HTMLElement} el  #duel-stage
 */
function mountLoadingVeil(el) {
	const veil = document.createElement('div');
	veil.className = 'duel-loading-veil';
	veil.innerHTML = '<div class="duel-loading-spinner"></div>';
	el.appendChild(veil);
	veilEl = veil;
}

/** Fade the loading veil out and remove it (idempotent). */
function hideLoadingVeil() {
	if (!veilEl) return;
	const veil = veilEl;
	veilEl = null;
	veil.classList.add('is-hidden');
	setTimeout(() => veil.remove(), 400);
}

/** The opening-turn "can't hit the opponent" notice + its auto-dismiss timer. */
let firstTurnTipEl = /** @type {HTMLElement|null} */ (null);
let firstTurnTipTimer = /** @type {ReturnType<typeof setTimeout>|null} */ (null);

/**
 * Fade a tooltip in over the OPPONENT'S half of the board (rows 0–1) explaining
 * the opening-turn rule, then auto-dismiss. Centered in that half and clamped
 * inside it with a margin. Called by targeting.js only when the picked source
 * could have hit the face but the rule blocked it and no creature was reachable.
 * @returns {void}
 */
function showFirstTurnTooltip() {
	const m = scene?.metrics;
	if (!stageEl || !m || !m.tileH) return;
	const MARGIN = 16;
	const boardW = m.tileW * 4 + m.gap * 3;
	const halfH = 2 * m.tileH + m.gap;            // rows 0 and 1 + the gap between
	const centerX = m.boardX + boardW / 2;
	const centerY = m.boardY + halfH / 2;

	if (!firstTurnTipEl || !firstTurnTipEl.isConnected) {
		firstTurnTipEl = document.createElement('div');
		firstTurnTipEl.className = 'duel-first-turn-tip';
		firstTurnTipEl.textContent =
			'You cannot target or attack an opponent during the very first turn of the game.';
		stageEl.appendChild(firstTurnTipEl);
	}
	const tip = firstTurnTipEl;
	tip.style.left = centerX + 'px';
	tip.style.top = centerY + 'px';
	tip.style.maxWidth = Math.max(120, boardW - MARGIN * 2) + 'px';
	tip.style.maxHeight = Math.max(40, halfH - MARGIN * 2) + 'px';
	// Restart the fade-in even if the tip is already up from a prior selection.
	tip.classList.remove('is-visible');
	void tip.offsetWidth; // force reflow so the transition re-runs
	tip.classList.add('is-visible');

	if (firstTurnTipTimer) clearTimeout(firstTurnTipTimer);
	firstTurnTipTimer = setTimeout(() => tip.classList.remove('is-visible'), 3200);
}

/** Tear the whole duel surface down (route change). */
export function destroy() {
	emptyWatcher?.();
	emptyWatcher = null;
	veilEl = null; // it lives inside stageEl, removed with the DOM below
	setAnimationScene(null);
	setDuelUpdateHandler(null); // socket falls back to store-only updates
	setTargetingChanged(() => {});
	setActionSent(null);
	setFirstTurnBlockedHandler(null);
	if (firstTurnTipTimer) { clearTimeout(firstTurnTipTimer); firstTurnTipTimer = null; }
	firstTurnTipEl = null; // lives inside stageEl, removed with the DOM below
	unlockInput(); // a pending send-lock must not outlive the screen
	unmountLog();
	if (scene) {
		window.removeEventListener('resize', scene.onResize);
		window.removeEventListener('orientationchange', scene.onResize);
		window.visualViewport?.removeEventListener('resize', scene.onResize);
		document.removeEventListener('fullscreenchange', scene.onResize);
		scene.resizeObserver?.disconnect();
		clearTimeout(scene.resizeTimer);
		for (const timer of scene.settleTimers || []) clearTimeout(timer);
		// Stop any in-flight game-over letter tweens before the objects vanish.
		if (scene.gameOverFx) {
			for (const t of scene.gameOverFx.letters || []) {
				gsap.killTweensOf(t);
				gsap.killTweensOf(t.scale);
			}
			gsap.killTweensOf(scene.gameOverFx.container);
		}
		if (scene.bunnyOverlay) gsap.killTweensOf(scene.bunnyOverlay.container);
		try {
			scene.app.destroy(true);
		} catch {
			// canvas already gone with the DOM — fine
		}
		scene = null;
	}
	exitGameFullscreen();
	stageEl = null;
}

// -----------------------------------------------------------------------------
// Scene lifecycle
// -----------------------------------------------------------------------------

/**
 * Build the PIXI application + scene object and paint the current duel.
 * @param {HTMLElement} el  #duel-stage
 */
async function mountScene(el) {
	// Route the duel's shared chrome through the one-shot bundle: if it's already
	// downloaded (warmed at boot) the frame/board/gui textures are seeded and the
	// loads below are free; if not, this fetches + unpacks it now, under the veil,
	// so the player still never watches it dribble in file by file. Fail-soft —
	// on any error the loads fall back to fetching each asset individually.
	await ensureStartupBundle();
	await initCardRenderer();

	const app = new PIXI.Application();
	await app.init({
		antialias: true,
		autoDensity: true,
		// Always render at 2x: cards are big containers minified hard (~0.2x),
		// and on DPR-1 desktops a 1x canvas made text mush and edges jagged.
		// (DPR-3 phones stay capped at 2 for performance, as before.)
		resolution: 2,
		// TRANSPARENT canvas: the dark backdrop comes from #duel-stage's
		// background-color (var(--bg)) showing through the empty area around the
		// battlefield frame, instead of an opaque canvas fill.
		backgroundAlpha: 0,
		resizeTo: el,
	});
	// The route may have changed while we awaited — bail out cleanly.
	if (!stageEl || stageEl !== el || !el.isConnected) {
		app.destroy(true);
		return;
	}
	el.appendChild(app.canvas);

	scene = {
		app,
		layers: {
			bg: new PIXI.Container(),      // the duel frame art, beneath everything
			board: new PIXI.Container(),
			boardOverlay: new PIXI.Container(), // targeting rings, above tile art
			hand: new PIXI.Container(),
			topBar: new PIXI.Container(),
			bottomBar: new PIXI.Container(),
			fx: new PIXI.Container(),
		},
		metrics: {},
		tiles: [],
		handCards: [],
		heros: store.heros,
		duelSpectators: store.duel?.spectatorCount || 0,
		refresh,
		// Debounced re-measure: mobile viewports settle in dribbles (URL bar
		// collapse, the fullscreen grant, orientation) — coalesce the storm,
		// resize the PIXI canvas, then re-layout.
		onResize: () => {
			clearTimeout(scene?.resizeTimer);
			if (!scene) return;
			scene.resizeTimer = setTimeout(() => {
				scene?.app?.resize?.();
				refresh();
			}, 80);
		},
		beginZoom,
		beginLeaderZoom: (heroIndex) => beginZoom(heroIndex, showLeaderZoom),
		endZoom,
		cancelZoom,
		makeCardSprite,
		showGameOver,
	};
	app.stage.addChild(
		scene.layers.bg,
		scene.layers.board, scene.layers.boardOverlay,
		scene.layers.topBar, scene.layers.bottomBar,
		// The hand draws ABOVE the bars: a selected card lifts up and can reach a
		// leader portrait, and it should ride OVER it rather than be clipped
		// behind the bar. (fx stays on top for flights/result letters.)
		scene.layers.hand,
		scene.layers.fx,
	);
	const frameReady = mountDuelFrame();

	// All duel updates now flow through the animation queue. Selection changes
	// take the cheap FEEDBACK pass (restyle persistent widgets in place) —
	// only actual state changes rebuild the scene. Rebuilding on every click,
	// like the old engine, made the whole hand flicker per selection.
	setAnimationScene(scene);
	setDuelUpdateHandler(onDuelUpdate);
	setTargetingChanged(repaintTargeting);
	// Every action costs a tick — the clock steps down the moment one is
	// SENT, so the cost reads as paid before the server answers.
	setActionSent(() => previewTickSpend(scene));
	setFirstTurnBlockedHandler(showFirstTurnTooltip);
	resetTargeting();

	// Re-measure on EVERY way the stage can change size — window resize alone
	// misses the mobile URL-bar collapse and the fullscreen grant, which left
	// the game laid out for a shorter screen with dead space at the bottom.
	window.addEventListener('resize', scene.onResize);
	window.addEventListener('orientationchange', scene.onResize);
	window.visualViewport?.addEventListener('resize', scene.onResize);
	document.addEventListener('fullscreenchange', scene.onResize);
	if (typeof ResizeObserver !== 'undefined') {
		scene.resizeObserver = new ResizeObserver(() => scene?.onResize());
		scene.resizeObserver.observe(el);
	}
	// Late-settling viewports (the URL bar eases away after load).
	scene.settleTimers = [setTimeout(scene.onResize, 500), setTimeout(scene.onResize, 1500)];
	requestMobileFullscreenOnFirstTap(el);
	// ?debug: expose the live scene (+ gsap, for animation tests) for inspection
	// (window.DB exists only when the debug panel is on — see app/debug.js).
	if (/** @type {any} */ (window).DB) {
		/** @type {any} */ (window).DB.scene = scene;
		/** @type {any} */ (window).DB.gsap = gsap;
	}
	const firstPaint = refresh();

	// Reveal the assembled scene in ONE piece: hold the dark veil up until the
	// battlefield frame art AND the first board/hand/bars paint have all landed
	// (or a safety timeout, so a hung asset can't strand the player on a blank
	// screen), then fade it out. Without this the duel visibly assembles part by
	// part; the veil makes it appear finished.
	const built = scene;
	void Promise.race([
		Promise.allSettled([frameReady, firstPaint]),
		new Promise((resolve) => setTimeout(resolve, 8000)),
	]).then(() => {
		if (scene === built) hideLoadingVeil();
	});

	// Warm OUR side's artwork in the background — hand, egg art, board, and
	// the whole deck list — so plays and hatches mount their tiles instantly
	// instead of fetching art mid-animation. (Opponent art loads on demand.)
	void preloadOwnDuelArts(store.duel, store.loadedDeck);

	// Safety net: if we mounted into an already-decided game (e.g. the result
	// arrived while we were in the lobby), show the result title right away.
	// Winner flags count too: an in-game win sets them immediately, while
	// GameOver only arrives on the final teardown push.
	if (store.duel?.GameOver || store.duel?.player1?.winner || store.duel?.player2?.winner) {
		showGameOver(store.duel);
	}

	// Single-player: get the bot brains loading NOW (they lazy-load), and if
	// the duel is already on the bot's move (Bot Game responses arrive via
	// HTTP, not a socket update, so the commit-path kick never saw them),
	// poke the runner. The PLAYER'S client runs the bot — a spectator's must
	// not: its state has the bot's hand stripped to a number, so TakeAction
	// crashes and the crash-guard spams actions the server drops anyway.
	if (store.duel?.format === 'Bot' && !store.duel.spectating) {
		void loadBots();
		kickBotIfNeeded();
	}
}

/**
 * Warm the duel's heavy, reused chrome BEFORE the first duel so entering one
 * doesn't stall while it downloads: the card/board frame textures (loaded by
 * initCardRenderer's generator init) and the big battlefield frame art
 * (gui/duel_bg.png). Best-effort, called from boot once the session is ready.
 * PIXI.Assets caches by URL and the generator init is idempotent, so the real
 * duel mount reuses all of this instantly instead of fetching it live.
 * @returns {Promise<void>}
 */
export async function warmDuelAssets() {
	try {
		// One request pulls every duel's shared chrome and seeds PIXI's cache…
		await ensureStartupBundle();
		// …so these resolve from memory (the generator's frame/board textures and
		// the battlefield art) instead of hitting the server.
		await initCardRenderer();
		await PIXI.Assets.load('/gui/duel_bg.png');
	} catch {
		// Best-effort warmup — the duel mount loads anything still missing.
	}
}

// The duel frame (gui/duel_bg.png, 2573×2590 — pixel-measured): bar strip
// interiors center at y 247.5 / 2341.5; circular portrait holes r=159 at
// (1287, 195) / (1287, 2394); the battlefield panel spans y 398–2189. To
// stretch UNIFORMLY at any screen shape it renders as THREE pieces: the two
// strip bands (plain sprites, uniform scale) anchored to their bar rows, and
// the panel between them as a 9-slice (borders uniform; only the flat purple
// middle stretches vertically to span whatever the board needs).
const FRAME = {
	w: 2573, h: 2590,
	sliceTop: 396,     // top piece = y 0..396 (strip + hole bulge + brackets)
	sliceBot: 2192,    // bottom piece = y 2192..2590
	stripCY: 247.5, stripBotCY: 2341.5,
	holeCY: 195, holeBotCY: 2394, holeR: 159,
	stripInnerH: 229,
};

/**
 * Load the duel frame art into the bg layer (once per mount); refresh()
 * repositions it via layoutDuelFrame on every layout pass.
 */
function mountDuelFrame() {
	const mountedScene = scene;
	if (!mountedScene) return Promise.resolve();
	return PIXI.Assets.load('/gui/duel_bg.png').then((texture) => {
		if (scene !== mountedScene || !scene.layers.bg) return;
		const src = texture.source;
		const piece = (y0, y1) => new PIXI.Texture({
			source: src,
			frame: new PIXI.Rectangle(0, y0, FRAME.w, y1 - y0),
		});
		const top = new PIXI.Sprite(piece(0, FRAME.sliceTop));
		const bottom = new PIXI.Sprite(piece(FRAME.sliceBot, FRAME.h));
		const panel = new PIXI.NineSliceSprite({
			texture: piece(FRAME.sliceTop, FRAME.sliceBot),
			leftWidth: 50, rightWidth: 50, topHeight: 60, bottomHeight: 60,
		});
		for (const part of [top, panel, bottom]) {
			part.eventMode = 'none'; // never intercept board/bar clicks
			scene.layers.bg.addChild(part);
		}
		scene.bgFrame = { top, panel, bottom };
		layoutDuelFrame();
	}).catch((error) => console.error('duel frame failed to load:', error));
}

/** Lay the three frame pieces onto the current metrics (every refresh). */
function layoutDuelFrame() {
	const frame = scene?.bgFrame;
	const bg = scene?.metrics?.bg;
	if (!frame || !bg || frame.top.destroyed) return;
	const { s, x, topY, botY } = bg;

	frame.top.scale.set(s);
	frame.top.position.set(x, topY);
	frame.bottom.scale.set(s);
	frame.bottom.position.set(x, botY);

	// The panel spans exactly between the two strip pieces; its 9-slice
	// borders render at the same uniform scale, only the middle stretches.
	const panelTop = topY + FRAME.sliceTop * s;
	frame.panel.scale.set(s);
	frame.panel.position.set(x, panelTop);
	frame.panel.width = bg.w / s;
	frame.panel.height = Math.max(1, (botY - panelTop) / s);
}

/** Compute the layout metrics for the current viewport (portrait-first). */
function computeMetrics() {
	if (!scene || !stageEl) return;
	const duel = store.duel;
	// Desktop with the log open reserves a side column for it (see duel css).
	const logSide = isLogOpen() && window.matchMedia('(min-width: 1100px)').matches ? 280 : 0;
	const width = Math.max(stageEl.clientWidth - logSide, 320);
	const height = Math.max(stageEl.clientHeight, 480);

	// The hand row exists whenever the state CARRIES a hand to show — the same
	// test renderHand uses. That includes spectating a bot game: the server
	// deliberately sends the human's full hand to its Judgy Spectators, and
	// sizing the row by `!duel.spectating` left renderHand painting those cards
	// into a 20px strip (a microscopic hand on top of the bottom portrait).
	const hasHand = duel && typeof duel.player1?.hand !== 'number';
	const topBarH = Math.max(44, Math.min(height * 0.08, 64));
	const botBarH = topBarH;
	const handH = hasHand ? Math.max(96, Math.min(height * 0.24, 235)) : 20;
	// Reserved headroom between the bottom bar and the hand: this is exactly
	// how far a selected card may RISE without sliding under the buttons.
	const handLiftRoom = hasHand ? Math.max(14, Math.min(34, handH * 0.18)) : 0;
	const gap = Math.max(4, Math.min(8, width * 0.008));

	// Board fits in what's left, capped by width; tiles keep the Board
	// texture's 683×515 landscape aspect (the old client's 171×129 cells).
	// marginY leaves room for the double-height portraits spilling out of
	// their bars toward the board. menuBand reserves the strip the DOM
	// Menu/log buttons overlay, so the opponent's bar NEVER has to shrink
	// or dodge them — it always starts below.
	const menuBand = 48;

	// --- Board + frame fit, settled together --------------------------------
	// The frame's PANEL has decorated borders (50px sides / 60px top+bottom
	// at native scale) that render at 50·s on screen — the battlefield must
	// sit INSIDE them (plus a gap of air) or the grid underlaps the frame
	// art and everything reads askew. The frame scale s depends on the board
	// size, which depends on marginY, which depends on s — two fixed-point
	// passes settle it.
	const BORDER_FRAC = 100 / FRAME.w;                // both side borders, as a width fraction
	const CLEAR_Y = FRAME.sliceTop + 60 - FRAME.stripCY; // bar center -> panel inner top (frame px)
	let marginY = topBarH * 0.62 + gap; // seed; recomputed in the fit loop below
	let boardAreaH;
	let tileH;
	let tileW;
	let boardW;
	let boardH;
	let bgW;
	let s;
	for (let pass = 0; pass < 2; pass++) {
		boardAreaH = height - menuBand - topBarH - botBarH - handH - handLiftRoom - marginY * 2;
		tileH = Math.max(40, Math.min(
			(boardAreaH - 3 * gap) / 4,
			((width * (1 - BORDER_FRAC) - 5 * gap) / 4) * (515 / 683),
		));
		tileW = tileH * (683 / 515);
		boardW = tileW * 4 + gap * 3;
		boardH = tileH * 4 + gap * 3;
		// Frame width: the board + its air gap sits exactly inside the
		// panel's side borders.
		bgW = Math.min(width, (boardW + gap * 2) / (1 - BORDER_FRAC));
		s = bgW / FRAME.w;
		// TOP flat margin = gap (equal to the side margin): the board sits exactly
		// a `gap` below the panel's inner top edge (CLEAR_Y*s down from the bar).
		// The double-height portrait lives in the strip ABOVE the flat area, so it
		// still clears the first row.
		marginY = Math.max(gap, CLEAR_Y * s - topBarH / 2) + gap;
	}

	// --- Vertical placement -------------------------------------------------
	// The frame now WRAPS the board with an equal `gap` flat margin on every side
	// — the 9-slice panel scrunches to fit, so there's no dead purple above/below
	// the tiles. The bottom bar rides just under the board; the hand fills the
	// rest below it. PORTRAIT banks any leftover height as a gap ABOVE the top bar
	// (thumbs hug the bottom); landscape/desktop centres it.
	const PANEL_BORDER = 60; // 9-slice top/bottom border px (matches mountDuelFrame)
	const belowBoard = gap + (PANEL_BORDER + FRAME.stripBotCY - FRAME.sliceBot) * s + botBarH / 2;
	const block = topBarH + marginY + boardH + belowBoard; // top-bar top -> bottom-bar bottom
	const isPortrait = height > width;
	const slack = Math.max(0, height - menuBand - block - handLiftRoom - handH - 4);
	const topBarY = menuBand + (isPortrait ? slack : slack / 2);

	const boardY = topBarY + topBarH + marginY;
	const boardBottom = boardY + boardH;
	const centerX = (width - boardW) / 2 + boardW / 2;
	const topBarCY = topBarY + topBarH / 2;
	const bgTopY = topBarCY - FRAME.stripCY * s;
	const bgBotY = boardBottom + gap + PANEL_BORDER * s;          // panel bottom hugs the board
	const botBarCY = bgBotY + (FRAME.stripBotCY - FRAME.sliceBot) * s;
	const botBarY = botBarCY - botBarH / 2;

	scene.metrics = {
		width,
		height,
		topBarH,
		topBarY,
		botBarH,
		gap,
		tileW,
		tileH,
		boardX: (width - boardW) / 2,
		boardY,
		botBarY,
		handY: botBarY + botBarH + handLiftRoom,
		handH,
		handLiftRoom,
		bg: { s, x: centerX - bgW / 2, w: bgW, topY: bgTopY, botY: bgBotY },
		barIconMax: Math.max(18, FRAME.stripInnerH * s - 6),
		portraitTop: { x: centerX, y: bgTopY + FRAME.holeCY * s },
		portraitBot: { x: centerX, y: bgBotY + (FRAME.holeBotCY - FRAME.sliceBot) * s },
		portraitD: 2 * FRAME.holeR * s + 4,
	};
}

/**
 * STATE pass: reconcile the scene against the current store.duel.
 *
 * Pseudo-React contract: board/hand/bars each keep their mounted objects and
 * re-render ONLY what a new state actually changed (keyed diffing — see
 * core/reconcile.js). A full remount happens only when the world itself
 * changes shape: first render, a different duel, or a layout/viewport change.
 */
function refresh() {
	if (!scene) return Promise.resolve();
	const duel = store.duel;
	if (!duel) return Promise.resolve();
	scene.duelSpectators = duel.spectatorCount || 0;
	computeMetrics();
	layoutDuelFrame();
	layoutBunnyOverlay();
	layoutGameOverFx();

	// Remount triggers: new duel, or the geometry every mounted object was
	// positioned with is gone (resize / log toggle / hand appears-disappears).
	const m = scene.metrics;
	const layoutKey = `${m.width}x${m.height}x${m.handH}x${duel.id}`;
	if (scene.layoutKey !== layoutKey) {
		scene.layoutKey = layoutKey;
		// Kill the bars' repeating flash timelines before orphaning their refs —
		// a repeat:-1 GSAP timeline keeps ticking (and firing on destroyed
		// widgets) otherwise, leaking one per resize.
		scene.barRefs?.devourFlash?.kill();
		scene.barRefs?.faceDevourFlash?.kill();
		scene.boardCells = null;
		scene.handCells = null;
		scene.handMount = null;
		scene.barRefs = null;
	}

	// Returned so the first mount can await a complete paint before revealing
	// the scene (mountScene's veil gate). Later refreshes ignore the promise.
	const painted = Promise.all([
		renderBars(scene, duel),
		renderBoard(scene, duel),
		renderHand(scene, duel),
	]);
	refreshChrome(); // keep the log's spectator count live
	return painted;
}

/** FEEDBACK pass: restyle the persistent targeting widgets in place. */
function repaintTargeting() {
	if (!scene) return;
	applyBoardTargeting(scene);
	applyHandTargeting(scene);
	applyBarTargeting(scene);
}

/**
 * Rasterize a card to a centered PIXI sprite (used by the event-fly
 * animation). Height ≈ 1.5 tiles.
 * @param {any} card
 * @returns {Promise<PIXI.Sprite>}
 */
async function makeCardSprite(card) {
	const img = await gen.renderCardToImage(resolveCardArt(card, true));
	const texture = PIXI.Texture.from(img);
	const sprite = new PIXI.Sprite(texture);
	sprite.anchor.set(0.5);
	const targetH = (scene?.metrics?.tileH || 120) * 1.5;
	sprite.scale.set(targetH / sprite.height);
	return sprite;
}

// -----------------------------------------------------------------------------
// DOM overlays: leave button, zoom popup, game over
// -----------------------------------------------------------------------------

/**
 * Mount the thin DOM chrome above the canvas.
 * @param {HTMLElement} el
 * @param {any} duel
 */
function mountOverlays(el, duel) {
	// Press-hold zoom popup (shared .popup styling from the old client).
	const popup = document.createElement('div');
	popup.className = 'popup';
	popup.id = 'duel-zoom-popup';
	el.appendChild(popup);

	// The game menu (replaces the bare concede button): lobby / deck /
	// settings / concede, with state-aware phrasing.
	const menu = document.createElement('button');
	menu.className = 'duel-leave-btn';
	menu.textContent = '☰ Menu';
	menu.addEventListener('click', openDuelMenu);
	el.appendChild(menu);

	// The action log (+ its toggle) — see log.js.
	mountLog(el, (card) => void showZoom(card));

	// "Help", right next to the log toggle: the How-To-Play reference sheet
	// (the same GUIDE_HTML the Guide screen renders — data/guide-content.js)
	// in a modal over the battle, for a quick mid-game rules peek.
	const help = document.createElement('button');
	help.className = 'duel-help-toggle';
	help.title = 'How to play, at a glance';
	help.textContent = '📖 Help';
	help.addEventListener('click', () => {
		openModal({
			title: 'How To Play',
			body: `<div class="guide-popup">${GUIDE_HTML}</div>`,
			buttons: [{ label: 'Close' }],
		});
	});
	el.appendChild(help);

	// Waiting notice for open-seat hosts.
	if (duel.player2?.nickname === '[No User]') {
		const waiting = document.createElement('div');
		waiting.className = 'duel-waiting';
		waiting.textContent = 'Waiting for an opponent to join…';
		el.appendChild(waiting);
	}
}

// -----------------------------------------------------------------------------
// The in-duel game menu.
// -----------------------------------------------------------------------------

/**
 * Fully leave the duel: tell the server (it finalizes/removes the game and
 * refreshes the lobby), clear it locally, go home. Used for concede, for
 * leaving a finished game, and for spectators stopping.
 */
function leaveDuelForGood() {
	click('Leave');
	setSlice('duel', null);
	navigate('lobby');
}

/**
 * The concede menu entry, phrased by game state — ported from the old
 * header's Leave button (:3824):
 *   you won                        -> "Leave 👑" (no confirm — just glory)
 *   bot game                       -> "Leave Game" (no stakes, no ceremony)
 *   your deck is empty             -> "Digest" (accept being devoured)
 *   endgame (no ticks / deck < 10) -> "Leave"
 *   otherwise                      -> "Ragequit"
 * @param {any} duel
 * @returns {{label: string, confirmTitle?: string, confirmBody?: string}}
 */
function concedePhrasing(duel) {
	if (duel.player1.winner) {
		return { label: 'Leave 👑' };
	}
	if (duel.player2.winner || duel.GameOver) {
		return { label: 'Leave' };
	}
	if (duel.format === 'Bot') {
		return { label: 'Leave Game' };
	}
	if (duel.player1.deck === 0) {
		return {
			label: 'Digest',
			confirmTitle: 'Digest?',
			confirmBody: 'Your deck is empty. Accept your fate and slide down your opponent\'s gullet?',
		};
	}
	if (duel.player1.tick + duel.player2.tick === 0 || duel.player1.deck < 10) {
		return {
			label: 'Leave',
			confirmTitle: 'Leave the duel?',
			confirmBody: 'Your opponent takes the win.',
		};
	}
	return {
		label: 'Ragequit',
		confirmTitle: 'Ragequit?',
		confirmBody: 'Your opponent devours you and takes the win.',
	};
}

/** Open the in-duel game menu popup. */
function openDuelMenu() {
	const duel = store.duel;
	if (!duel) return;
	const spectating = !!duel.spectating;
	const over = !!(duel.GameOver || duel.player1?.winner || duel.player2?.winner);
	const buttons = [];

	// Return to lobby: TEMPORARY while the game is live (rejoin via the nav's
	// Game button or the lobby's Load row) — FINAL once the game is decided.
	buttons.push({
		label: over ? 'Back To Lobby' : 'Return To Lobby',
		onClick: () => {
			if (over || spectating) {
				leaveDuelForGood();
			} else {
				navigate('lobby'); // duel stays live; you can come back
			}
		},
	});

	// Your decklist (players only — spectators have no deck in the game).
	if (!spectating && store.loadedDeck?.list) {
		buttons.push({ label: 'View Your Deck', onClick: showDeckOverview });
	}

	if (can('settings')) {
		buttons.push({ label: 'Settings', onClick: () => navigate('settings') });
	}

	if (spectating) {
		buttons.push({ label: 'Stop Watching', onClick: leaveDuelForGood });
	} else {
		const phrasing = concedePhrasing(duel);
		buttons.push({
			label: phrasing.label,
			onClick: () => {
				if (!phrasing.confirmBody) {
					// The game is already decided — leave without ceremony.
					leaveDuelForGood();
					return;
				}
				openModal({
					title: phrasing.confirmTitle,
					body: phrasing.confirmBody,
					buttons: [
						{ label: phrasing.label, onClick: leaveDuelForGood },
						{ label: 'Keep Fighting' },
					],
				});
			},
		});
	}

	buttons.push({ label: 'Close' });
	openModal({ title: 'Game Menu', buttons });
	// Buttons stack full-width via .modal-actions (see ui/modal.js + CSS).
}

/**
 * "View Your Deck": the decklist you brought to this duel (store.loadedDeck),
 * quantity-grouped like the clipboard export. The REMAINING deck order is
 * hidden information — the server only ever sends a count — so this is the
 * full list, not a peek at what's left.
 */
function showDeckOverview() {
	const deck = store.loadedDeck;
	if (!deck?.list) return;
	/** @type {Array<{name: string, count: number}>} */
	const grouped = [];
	for (const entry of deck.list) {
		const name = store.cards[entry.id]?.name || 'Unknown';
		const last = grouped.at(-1);
		if (last && last.name === name) {
			last.count++;
		} else {
			grouped.push({ name, count: 1 });
		}
	}
	const body = document.createElement('div');
	body.style.cssText = 'max-height:50dvh;overflow-y:auto;text-align:left;line-height:1.7;';
	body.innerHTML = grouped
		.map(({ name, count }) => `● ${escapeHtml(name)} ×${count}`)
		.join('<br>');
	openModal({
		title: `${deck.nickname || 'Your Deck'} (${deck.list.length} cards)`,
		body,
		buttons: [{ label: 'Back To Game' }],
	});
}

/** Zoom state (old canZoom/canClick :4744). */
let zoomTimer = 0;
let zoomShown = false;

/**
 * Arm the press-hold zoom (fires after 250ms of holding).
 * @param {any} subject           A card — or whatever `shower` expects.
 * @param {(s: any) => any} [shower]  Popup filler; defaults to the card zoom.
 */
function beginZoom(subject, shower = showZoom) {
	cancelZoom();
	zoomTimer = window.setTimeout(() => void shower(subject), 250);
}

/**
 * Pointer released: if the zoom was showing, close it and CONSUME the tap.
 * @returns {boolean} true when the zoom consumed this press.
 */
function endZoom() {
	window.clearTimeout(zoomTimer);
	if (zoomShown) {
		hideZoom();
		return true;
	}
	return false;
}

/** Abort a pending zoom without consuming anything. */
function cancelZoom() {
	window.clearTimeout(zoomTimer);
	if (zoomShown) hideZoom();
}

/**
 * Show the big rendered card (old PlanZoom :4746). Own eggs zoom to their
 * hidden trueForm; the opponent's stay secret (server already stripped them).
 * @param {any} card
 */
async function showZoom(card) {
	const popup = document.getElementById('duel-zoom-popup');
	if (!popup) return;
	let data = card;
	if (data.trueForm && data.trueForm !== true && data.owner === store.duel?.player1?.id) {
		data = data.trueForm;
	}
	try {
		const img = await gen.renderCardToImage(resolveCardArt(data, true));
		popup.innerHTML = '';
		popup.appendChild(img);
		popup.style.display = 'flex';
		popup.classList.add('show');
		zoomShown = true;
	} catch (error) {
		console.error('card zoom failed:', error);
	}
}

/**
 * Show the full leader card (press-hold a bar portrait). The /leaders/ PNGs
 * ARE the complete cards — name, rules text, and faction are baked in.
 * @param {number} heroIndex
 */
function showLeaderZoom(heroIndex) {
	const popup = document.getElementById('duel-zoom-popup');
	if (!popup) return;
	const img = document.createElement('img');
	img.src = `/leaders/${HERO_ASSET_NAMES[heroIndex] || 'john'}.png`;
	img.alt = 'Leader card';
	popup.innerHTML = '';
	popup.appendChild(img);
	popup.style.display = 'flex';
	popup.classList.add('show');
	zoomShown = true;
}

/** Hide the zoom popup. */
function hideZoom() {
	const popup = document.getElementById('duel-zoom-popup');
	if (popup) {
		popup.classList.remove('show');
		popup.style.display = 'none';
	}
	zoomShown = false;
}

/**
 * The result line, per viewer (duel.player1 is always the viewer's side;
 * spectators watch from the host's side with duel.spectating set):
 *   spectator -> "<winner> won"
 *   winner    -> "Victorious"
 *   loser     -> "Defeat" — or "Devoured" when their deck ran empty
 *                (they were eaten, not merely beaten).
 * A decided-less GameOver (the stale-game reaper) reads neutral.
 * @param {any} duel
 * @returns {string}
 */
function gameOverTitle(duel) {
	const winner = duel.player1?.winner ? duel.player1
		: duel.player2?.winner ? duel.player2 : null;
	if (!winner) return 'Game over';
	if (duel.spectating) {
		return `${winner.nickname || 'Someone'} won`;
	}
	if (winner === duel.player1) return 'Victorious';
	return duel.player1?.deck === 0 ? 'Devoured' : 'Defeat';
}

/**
 * The end-of-game display: the result title, rendered in PIXI over the
 * battlefield, its letters tweening in one by one. No extra chrome — the game
 * menu already carries the way home ("Back To Lobby" once the game is decided).
 *   win        -> bouncy, letters pop up (you earned it)
 *   loss       -> heavy, letters drop from above and thud down (sad)
 *   spectator  -> a plain fade-and-rise (you're just watching)
 * @param {any} duel
 */
function showGameOver(duel) {
	const winner = duel.player1?.winner ? duel.player1
		: duel.player2?.winner ? duel.player2 : null;
	const mode = duel.spectating ? 'spectator'
		: winner === duel.player1 ? 'win'
			: 'loss';
	// BUNNY VICTORY: the rare Rabbit Revolution alt-win (100 Health of Fluffles)
	// drops a chosen ARTWORK over the whole battlefield as a surprise overlay.
	if (duel.player1?.altWin === 'Rabbit Revolution' || duel.player2?.altWin === 'Rabbit Revolution') {
		void mountBunnyOverlay(duel);
	}
	mountGameOverFx(gameOverTitle(duel), mode);
}

/**
 * Mount the result title on the fx layer (topmost — above bars and fliers) as
 * ONE PIXI.Text per letter, so each can animate on its own. layoutGameOverFx()
 * keeps the word centered + fitted on every layout pass.
 * @param {string} title
 * @param {'win'|'loss'|'spectator'} mode
 */
function mountGameOverFx(title, mode = 'spectator') {
	if (!scene || scene.gameOverFx) return;
	const style = {
		fontFamily: 'Luckiest Guy',
		fontSize: 64,
		fill: mode === 'loss' ? 0xd9cfe6 : 0xffffff, // defeat reads a touch ashen
		letterSpacing: 2,
		dropShadow: { color: 0x000000, alpha: 0.8, blur: 9, distance: 4, angle: Math.PI / 2 },
	};
	const container = new PIXI.Container();
	// The result word always LOOKED tappable — so make it do something: a tap
	// opens the game menu (the way back to the lobby / a rematch). Works for
	// win, loss, and spectator alike.
	container.eventMode = 'static';
	container.cursor = 'pointer';
	const letters = [];
	for (const ch of [...title]) {
		const t = new PIXI.Text({ text: ch === ' ' ? ' ' : ch, style: { ...style } });
		t.anchor.set(0.5);
		letters.push(t);
		container.addChild(t);
	}
	// Lay the letters in a row centered on the container origin; remember each
	// resting spot so the tweens can offset from it.
	const spacing = 2;
	const totalW = letters.reduce((sum, t) => sum + t.width, 0) + spacing * (letters.length - 1);
	let x = -totalW / 2;
	for (const t of letters) {
		t.x = x + t.width / 2;
		t.y = 0;
		t.baseY = 0;
		x += t.width + spacing;
	}
	// One generous rectangle over the whole word: the letters animate and have
	// gaps, so hit-testing the glyphs alone would feel finicky. In LOCAL space,
	// so it scales with the container in layoutGameOverFx.
	const hitH = Math.max(style.fontSize, ...letters.map((t) => t.height || 0));
	container.hitArea = new PIXI.Rectangle(-totalW / 2 - 24, -hitH / 2 - 14, totalW + 48, hitH + 28);
	container.on('pointertap', () => openDuelMenu());
	scene.layers.fx.addChild(container);
	scene.gameOverFx = { container, letters, mode, totalW };
	layoutGameOverFx();
	animateGameOverFx();
}

/**
 * Center the word on the battlefield (board midpoint) and scale it to fit the
 * board width. Scaling the CONTAINER (not per-letter fontSize) leaves each
 * letter's own tweened scale/position free for the animation.
 */
function layoutGameOverFx() {
	const fx = scene?.gameOverFx;
	const m = scene?.metrics;
	if (!fx || fx.container.destroyed || !m?.tileW) return;
	const boardW = m.tileW * 4 + m.gap * 3;
	const boardH = m.tileH * 4 + m.gap * 3;
	fx.container.scale.set(Math.min(1, (boardW * 0.92) / (fx.totalW || 1)));
	fx.container.position.set(m.boardX + boardW / 2, m.boardY + boardH / 2);
}

/**
 * Choose the Rabbit Revolution ARTWORK for the victory overlay (owner's
 * spec): a MamaBliss piece, as lewd as the player's censor settings allow,
 * in the player's preferred flavor.
 *   1. Candidates = the card's MamaBliss `newarts` entries whose content
 *      flags pass the censor. Each entry collapses to ONE file: its
 *      enabled gender variant ranked by settings.priority (generic fits
 *      everyone).
 *   2. If any allowed candidate is nude/smut, the pool narrows to those —
 *      lewd when permitted, wholesome when censored.
 *   3. The game-winning copy's own artwork wins when it made the pool
 *      (duel.history.event carries the played card); otherwise a pick
 *      seeded by the duel id — stable across re-mounts, varied per game.
 * Returns null when nothing qualifies (no newarts yet, everything
 * censored…) — the caller falls back to the old SRC chain.
 * @param {any} duel
 * @param {any} card  The Rabbit Revolution base card (store.cards entry).
 * @returns {string|null}  An art filename, or null.
 */
function pickBunnyArt(duel, card) {
	try {
		const settings = store.userData?.settings;
		const META_KEYS = ['art_id', 'credit', 'chance', 'flavor', 'safe', 'nude', 'vore', 'gore', 'smut', 'notes'];
		const censored = (entry) => ['nude', 'smut', 'vore', 'gore']
			.some((flag) => entry[flag] && settings?.censor?.[flag]);
		const genderOk = (file) => {
			const suffix = String(file).slice(0, String(file).lastIndexOf('.')).split('_').slice(3).join('_');
			return !suffix || suffix === 'generic' || settings?.gender?.[suffix] !== false;
		};
		const priority = Array.isArray(settings?.priority) && settings.priority.length
			? settings.priority : ['male', 'female', 'gyno', 'futa'];
		const rank = (file) => {
			const first = String(file).split('_')[3]?.split('.')[0];
			const index = priority.indexOf(first);
			return index === -1 ? priority.length : index;
		};

		const pool = [];
		for (const entry of (card.newarts || [])) {
			if (String(entry.credit).toLowerCase() !== 'mamabliss' || censored(entry)) continue;
			const files = Object.keys(entry)
				.filter((key) => !META_KEYS.includes(key) && typeof entry[key] === 'string' && entry[key])
				.map((key) => entry[key])
				.filter(genderOk)
				.sort((a, b) => rank(a) - rank(b));
			if (files.length) pool.push({ file: files[0], lewd: !!(entry.nude || entry.smut) });
		}
		if (!pool.length) return null;
		const lewd = pool.filter((candidate) => candidate.lewd);
		const files = (lewd.length ? lewd : pool).map((candidate) => candidate.file);

		const played = duel?.history?.event;
		if (played && played.id === card.id && files.includes(played.selectedArt)) {
			return played.selectedArt;
		}
		let seed = 0;
		for (const ch of String(duel?.id || '')) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
		return files[seed % files.length];
	} catch (error) {
		console.error('pickBunnyArt failed:', error);
		return null;
	}
}

/**
 * BUNNY VICTORY overlay: a Rabbit Revolution ARTWORK (not the whole card —
 * see pickBunnyArt), stretched to cover the whole 4×4 board (same rounded
 * corners), fading in over the tiles — BENEATH the result letters. Lives in
 * the fx layer, so it's torn down with the scene when the duel screen is
 * left. Idempotent.
 * @param {any} duel  The winning duel state (for history + a stable seed).
 * @returns {Promise<void>}
 */
async function mountBunnyOverlay(duel) {
	if (!scene || scene.bunnyOverlay) return;
	const card = store.cards?.find((c) => c && c.name === 'Rabbit Revolution');
	if (!card) return;
	// The RAW artwork file — never the card. (image-cache SRC returns the
	// bucket's pre-rendered full-card "Card_<art>.png"; this overlay is the
	// one consumer that must NOT use it. The bare art file sits at the bucket
	// root under its own name.) When no MamaBliss candidate qualifies, run
	// the legacy SRC chain purely to RESOLVE a file, then still show that
	// file's raw artwork.
	let file = pickBunnyArt(duel, card);
	if (!file) {
		const probe = { ...card };
		SRC(probe, store.cards); // resolves probe.selectedArt via the legacy chain
		file = probe.selectedArt;
	}
	if (!file) return;
	const url = ORIGIN_PREFIX + file;
	try {
		const texture = await PIXI.Assets.load(url);
		if (!scene || scene.bunnyOverlay) return; // route changed / already mounted
		const container = new PIXI.Container();
		container.eventMode = 'none';
		container.alpha = 0;
		const sprite = new PIXI.Sprite(texture);
		const mask = new PIXI.Graphics();
		sprite.mask = mask;
		container.addChild(sprite, mask);
		scene.layers.fx.addChildAt(container, 0); // below the result letters
		scene.bunnyOverlay = { container, sprite, mask, texture };
		layoutBunnyOverlay();
		gsap.to(container, { alpha: 1, duration: 0.9, ease: 'power2.out' });
	} catch (error) {
		console.error('bunny overlay art failed to load:', error);
	}
}

/** Cover the board with the bunny art (rounded to the board), on every layout. */
function layoutBunnyOverlay() {
	const o = scene?.bunnyOverlay;
	const m = scene?.metrics;
	if (!o || o.container.destroyed || !m?.tileW) return;
	const boardW = m.tileW * 4 + m.gap * 3;
	const boardH = m.tileH * 4 + m.gap * 3;
	const tw = o.texture.width || 1;
	const th = o.texture.height || 1;
	const scale = Math.max(boardW / tw, boardH / th); // COVER the board
	o.sprite.scale.set(scale);
	o.sprite.x = m.boardX + (boardW - tw * scale) / 2;
	o.sprite.y = m.boardY + (boardH - th * scale) / 2;
	const r = Math.min(14, m.tileW * 0.06);
	o.mask.clear().roundRect(m.boardX, m.boardY, boardW, boardH, r).fill(0xffffff);
}

/**
 * Run the per-letter entrance for the current outcome (see showGameOver).
 */
function animateGameOverFx() {
	const fx = scene?.gameOverFx;
	if (!fx) return;
	const { container, letters, mode } = fx;
	if (mode === 'spectator') {
		// Simple: the whole line fades in and rises a touch.
		gsap.fromTo(container, { alpha: 0 }, { alpha: 1, duration: 0.6, ease: 'power2.out' });
		gsap.from(container, { y: container.y + 16, duration: 0.6, ease: 'power2.out' });
		return;
	}
	const stagger = 0.06;
	letters.forEach((t, i) => {
		if (mode === 'win') {
			// Bouncy fun: each letter springs up from nothing with an overshoot.
			t.alpha = 1;
			t.scale.set(0);
			gsap.to(t.scale, { x: 1, y: 1, duration: 0.5, ease: 'back.out(2.6)', delay: i * stagger });
			gsap.fromTo(t, { y: t.baseY - 26 }, { y: t.baseY, duration: 0.55, ease: 'bounce.out', delay: i * stagger });
		} else {
			// Defeat: letters drop in from above and thud down one by one — heavy,
			// accelerating, a small droop straightening out. No bounce.
			t.alpha = 0;
			gsap.to(t, { alpha: 1, duration: 0.18, delay: i * stagger });
			gsap.fromTo(t,
				{ y: t.baseY - 46, rotation: -0.14 },
				{ y: t.baseY, rotation: 0, duration: 0.5, ease: 'power2.in', delay: i * stagger });
		}
	});
}
